<script lang="ts">
	import { slideIn } from '$lib/actions/slideIn';
	import Button from '$lib/buttons/v3/button.svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import { editable } from '$lib/storyblok/editable';
	import type { SbImage, SbLink } from '$lib/storyblok/types';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';

	interface Props {
		content: ISbStoryData<{
			headline: string | ISbRichtext;
			subline: ISbRichtext;
			cta_link: SbLink;
			cta_text: string;
			image: SbImage;
		}>['content'];
	}

	let { content }: Props = $props();
</script>

<section
	use:editable={content}
	class="mt-8 bg-white px-3 pb-3 text-brown-1 lg:mt-32 lg:px-5 lg:pb-5"
>
	<div class="grid-container overflow-hidden rounded-2xl max-lg:px-4 max-lg:pt-4 lg:rounded-3xl">
		<PageGrid16>
			<div
				class="col-span-full flex flex-col items-center text-pretty text-center lg:col-start-3 lg:col-end-[15] xl:col-start-4 xl:col-end-[14]"
			>
				<div
					class="mb-3 text-[44px]/[108%] tracking-[-.44px] text-brown-1 lg:text-[64px]/[110%] lg:tracking-[-.64px]"
					use:slideIn
				>
					<SbRichtext text={content.headline} />
				</div>
				<div
					class="mt-3 text-[16px]/[150%] tracking-[-.16px] text-brown-4 lg:mt-5 lg:text-[20px]/[140%] lg:tracking-[-.2px]"
					use:slideIn={{ delay: 100 }}
				>
					<SbRichtext text={content.subline} />
				</div>
				<div use:slideIn={{ delay: 200 }}>
					<Button class="btn-large btn-brown mt-7 lg:mt-10" href={getSbUrl(content.cta_link)}
						>{content.cta_text}</Button
					>
				</div>
			</div>
			<div
				class="col-span-full mt-8 lg:col-start-3 lg:col-end-[15] lg:mt-24"
				use:slideIn={{ delay: 400, threshold: 0.1 }}
			>
				<Image
					class="min-h-[100px]"
					src={content.image.filename}
					alt={content.image.alt}
					title={content.image.title}
				/>
			</div>
		</PageGrid16>
	</div>
</section>

<style>
	div.grid-container {
		background: linear-gradient(180deg, rgba(245, 245, 240, 0) 0%, #f5f5f0 100%);

		@media (min-width: 1024px) {
			background: linear-gradient(180deg, rgba(245, 245, 240, 0) 0%, #faf9f9 100%);
		}
	}
</style>
